import { useSortable } from '@dnd-kit/react/sortable';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import ErrorIcon from '@mui/icons-material/Error';
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  ListItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChangeEvent, useRef, useState } from 'react';

import { TemplatePlaceholderWithNodeAndFilterContext } from './types';

type PlaceholderFieldContentProps = {
  placeholderData: TemplatePlaceholderWithNodeAndFilterContext;
  onChange: (placeholder: TemplatePlaceholderWithNodeAndFilterContext) => void;
  /** When true, this placeholder is in a filter whose primary field is empty; show error state. */
  isHidden?: boolean;
  /** Called when the user clicks the card while isHidden; e.g. scroll to filter in tree. */
  onClickScrollToFilter?: () => void;

  /** Called when the "Add instructions" field gains focus; highlights corresponding filter on left and scrolls to it. */
  onPlaceholderClick?: () => void;

  /** Called when the "Add instructions" field loses focus; clears the highlight. */
  onPlaceholderBlur?: () => void;

  /** When true, this placeholder is selected (focused); show border on right. */
  isSelected?: boolean;

  /** When provided, used only for ARIA (e.g. drag-handle aria-label), not for visible text. Enables disambiguation for screen readers. */
  ariaTitle?: string;

  sortId: string;
  sortIndex: number;
};

const MAX_PROMPT_TEXT_LENGTH = 500;

const HIDDEN_PLACEHOLDER_TOOLTIP = 'This placeholder is in a filter that is incorrectly configured';

export default function PlaceholderFieldContent({
  placeholderData,
  onChange = () => {},
  isHidden = false,
  onClickScrollToFilter,
  onPlaceholderClick,
  onPlaceholderBlur,
  isSelected = false,
  ariaTitle,
  sortId,
  sortIndex,
}: PlaceholderFieldContentProps) {
  const [element, setElement] = useState<Element | null>(null);

  const dragHandleRef = useRef<HTMLButtonElement | null>(null);
  // TODO: Use the isDragging to add styling?
  // const { isDragging } = useSortable({
  useSortable({
    id: sortId,
    index: sortIndex,
    element,
    handle: dragHandleRef,
  });

  const handleChangeDescription = (description: string) => {
    onChange({
      ...placeholderData,
      placeholder: {
        ...placeholderData.placeholder,
        description,
      },
    });
  };

  const handleChangeOptional = (optional: boolean) => {
    onChange({
      ...placeholderData,
      placeholder: {
        ...placeholderData.placeholder,
        optional,
      },
    });
  };

  const ariaLabelTitle = ariaTitle ?? placeholderData.placeholder.title;
  const dragHandleLabel = `Drag handle for ${ariaLabelTitle}`;

  const cardContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px"
      }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: '10px',
          alignItems: "center"
        }}>
        <IconButton ref={dragHandleRef} aria-label={dragHandleLabel}>
          <DragHandleIcon sx={{ height: '24px', width: '24px' }} color="secondary" />
        </IconButton>

        <Typography
          component="h3"
          variant="body1"
          sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}
        >
          <span>
            <strong>{placeholderData.placeholder.title}</strong>
          </span>
        </Typography>
        {isHidden && (
          <Tooltip title={HIDDEN_PLACEHOLDER_TOOLTIP}>
            <ErrorIcon
              color="error"
              sx={{ fontSize: 24 }}
              aria-label={HIDDEN_PLACEHOLDER_TOOLTIP}
            />
          </Tooltip>
        )}
      </Box>
      <TextField
        sx={{ minWidth: '348px' }}
        label="Add instructions"
        variant="filled"
        multiline
        minRows={2}
        helperText="Explain to the user why they are selecting this field"
        value={placeholderData.placeholder.description ?? ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          handleChangeDescription(event.target.value);
        }}
        onFocus={() => onPlaceholderClick?.()}
        onBlur={() => onPlaceholderBlur?.()}
        disabled={isHidden}
        slotProps={{
          htmlInput: {
            maxLength: MAX_PROMPT_TEXT_LENGTH,
          },
        }}
      />
      <FormControlLabel
        // For future task
        sx={{ display: 'none' }}
        control={
          <Checkbox
            color="secondary"
            checked={placeholderData.placeholder.optional}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              handleChangeOptional(event.target.checked);
            }}
          />
        }
        label="Optional"
      />
    </Box>
  );

  const selectedBorder = { border: '2px solid rgb(102, 45, 145)', borderRadius: '4px' };
  const wrapperSx = isHidden
    ? {
        border: '2px solid',
        padding: '8px',
        borderColor: 'error.main',
        borderRadius: '4px',
        cursor: onClickScrollToFilter ? 'pointer' : undefined,
        ...(isSelected && selectedBorder),
      }
    : {
        padding: '8px',
        border: '2px solid transparent',
        borderRadius: '4px',
        ...(isSelected && selectedBorder),
      };

  // Card click only for hidden placeholders to scroll to filter (no purple border); non-hidden use TextField focus/blur
  const clickHandler = isHidden ? onClickScrollToFilter : undefined;

  return (
    <ListItem
      ref={setElement}
      sx={{
        paddingLeft: 0,
        paddingRight: 0,
        marginLeft: 0,
        marginRight: 0,
      }}
      aria-label={ariaLabelTitle}
    >
      {clickHandler ? (
        <Paper
          sx={{
            ...wrapperSx,
            cursor: 'pointer',
            marginLeft: '0',
            marginRight: '0',
          }}
          onClick={clickHandler}
          role="button"
          aria-label="Scroll to filter in search tree"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              clickHandler();
            }
          }}
        >
          {cardContent}
        </Paper>
      ) : (
        <Paper sx={wrapperSx}>{cardContent}</Paper>
      )}
    </ListItem>
  );
}
